import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBarcode } from '@fortawesome/pro-light-svg-icons';
import { useToast } from '@shared/hooks/use-toast';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';

interface CartScannerProps {
  onCartValidated?: (cartCode: string, cartData: any) => void;
}

/**
 * Scanner de carrito - Componente para escanear códigos de carrito
 *
 * Permite escanear o escribir manualmente el código de un carrito
 * para validarlo y comenzar el proceso de trabajo
 */
export const CartScanner = ({ onCartValidated }: CartScannerProps) => {
  const { t } = useTranslation('extraWorkOrdersModule');
  const [cartCode, setCartCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus al montar el componente
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const { toast } = useToast();

  const handleValidate = async () => {
    if (!cartCode.trim() || isValidating) return;

    setIsValidating(true);
    try {
      console.log('🔍 CartScanner - Buscando carrito:', cartCode.trim());

      // Buscar carrito en la API
      const httpClient = createHttpClient(apiClientWH);
      const response = await httpClient.get<any>('/carts', {
        search: cartCode.trim(),
        per_page: 1,
      });

      console.log('🔍 CartScanner - Respuesta API:', response);

      // La respuesta viene como { data: Cart[], meta: {...} }
      const carts = response.data?.data || response.data || [];

      // Verificar si se encontró el carrito
      if (Array.isArray(carts) && carts.length > 0) {
        const cart = carts[0];

        // Verificar que el código coincida (case-insensitive)
        if (cart.code?.toLowerCase() === cartCode.trim().toLowerCase()) {
          // Validar que el carrito no esté ocupado: stock_location_carts con elementos indica que ya está en uso
          const stockLocationCarts = cart.stock_location_carts;
          const isCartOccupied = Array.isArray(stockLocationCarts) && stockLocationCarts.length > 0;

          if (isCartOccupied) {
            toast({
              title: t('detail.scanner.error.title', 'Error'),
              description: t('detail.scanner.error.cart_occupied', 'El carrito está ocupado y no se puede usar'),
              variant: 'destructive',
            });
            setCartCode('');
            setTimeout(() => {
              inputRef.current?.focus();
            }, 100);
            return;
          }

          console.log('✅ CartScanner - Carrito encontrado:', cart);
          console.log('🔍 CartScanner - Código del carrito (cart.code):', cart.code);
          console.log('🔍 CartScanner - Código escaneado (cartCode):', cartCode.trim());

          // Mostrar toast de éxito
          toast({
            title: t('detail.scanner.success.title', 'Éxito'),
            description: t('detail.scanner.success.cart_validated', 'Carrito validado correctamente'),
            variant: 'default',
          });

          // Llamar callback con el código del carrito (cart.code) como location_code
          // El código del carrito es el código de ubicación que necesita el endpoint
          onCartValidated?.(cart.code || cartCode.trim(), cart);
        } else {
          // El código no coincide exactamente
          toast({
            title: t('detail.scanner.error.title', 'Error'),
            description: t('detail.scanner.error.not_found', 'Carrito no encontrado'),
            variant: 'destructive',
          });

          setCartCode('');
          setTimeout(() => {
            inputRef.current?.focus();
          }, 100);
        }
      } else {
        // No se encontró el carrito
        toast({
          title: t('detail.scanner.error.title', 'Error'),
          description: t('detail.scanner.error.not_found', 'Carrito no encontrado'),
          variant: 'destructive',
        });

        // Limpiar input y reenfocar
        setCartCode('');
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      }
    } catch (error: any) {
      console.error('❌ CartScanner - Error al buscar carrito:', error);

      // Mostrar toast de error
      toast({
        title: t('detail.scanner.error.title', 'Error'),
        description:
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          t('detail.scanner.error.unexpected', 'Error inesperado al buscar el carrito'),
        variant: 'destructive',
      });

      // Limpiar input y reenfocar
      setCartCode('');
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    } finally {
      setIsValidating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleValidate();
    }
  };

  return (
    <div className="bg-card-bg border border-card-border rounded-lg p-6 space-y-4">
      {/* Título - Centrado */}
      <h2 className="text-xl font-bold text-title text-center">{t('detail.scanner.title', 'Escáner de Códigos')}</h2>

      {/* Instrucciones - Centrado */}
      <p className="text-sm text-complementary-text text-center !mt-1">
        {t('detail.scanner.instructions', 'Escanea el código del carrito para comenzar')}
      </p>

      {/* Input de escaneo */}
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-icons">
          <FontAwesomeIcon icon={faBarcode} className="w-4 h-4" />
        </span>
        <Input
          ref={inputRef}
          placeholder={t('detail.scanner.placeholder', 'Escanea o escribe el código del carrito...')}
          value={cartCode}
          onChange={(e) => setCartCode(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isValidating}
          className="w-full pl-10 text-center"
          data-testid="cart-scanner-input"
        />
      </div>

      {/* Botón de validar - Centrado */}
      <Button
        variant="primary"
        size="lg"
        onClick={handleValidate}
        disabled={!cartCode.trim() || isValidating}
        className="w-full"
        data-testid="validate-cart-button"
      >
        {isValidating
          ? t('detail.scanner.validating', 'Validando...')
          : t('detail.scanner.validate', 'Validar Carrito')}
      </Button>
    </div>
  );
};
